import React, { useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { Helmet } from 'react-helmet'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import { Group as GroupIcon } from '@mui/icons-material'
import {
  TableCell,
  Table,
  TableRow,
  TableBody,
  TableHead,
  Paper,
  Button,
  Grid,
  DialogContent,
  DialogActions,
  TextField,
  Dialog,
  Typography,
  FormControl,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  AutocompleteRenderInputParams,
} from '@mui/material'
import { AddCircle, Edit } from '@mui/icons-material'
import { IReferralSourceSummary } from 'src/viewModels/referrals/ReferralSourceSummary'
import {
  searchReferralSource,
  addReferralSource,
  getReferralSourceSpecialties,
} from 'src/services/AdminService'
import { pagedList } from 'src/viewModels/pagedList'
import { ReferralSourceDTO } from 'src/viewModels/referrals/ReferralSourceDTO'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import FullWidthField from 'src/common/FullWidthField'
import SelectInput from 'src/common/SelectInput/SelectInput'
import States from 'src/utils/States'
import PhoneNumberMaskOBM from 'src/common/PhoneNumberMaskOBM'
import moment from 'moment'

const ReferralSource: React.FC = () => {
  const [gridData, setGridData] = useState<IReferralSourceSummary[]>(
    new pagedList().items
  )
  const [gridLoading, setGridLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)

  //Search
  const [referralSpeciality, setReferralSpeciality] = useState('')
  const [inActive, setInActive] = useState(false)
  const [sourceName, setSourceName] = useState('')

  //Pagination
  const [totalItems, setTotalItems] = useState(0)
  const [firstRecordIndex, setFirstRecordIndex] = useState(0)
  const [lastRecordIndex, setLastRecordIndex] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [specialityOptions, setSpecialityOptions] = useState<string[]>([' '])
  const states: string[] = States.map((state) => state.name)
  const dropdownState: string[] = [...[' '], ...states]

  const validationSchema = Yup.object().shape({
    newSource: Yup.string().required('Referral Source is required'),
    newSpeciality: Yup.string(),
    newBillingAddress: Yup.string(),
    newBillingCity: Yup.string(),
    newBillingState: Yup.string(),
    newBillingZip: Yup.string(),
    newReferralSourceEmail: Yup.string(),
    newBillingPhone: Yup.string(),
    newBillingFax: Yup.string(),
  })

  const formatBillingDetails = (
    billingAddress: string | undefined,
    billingCity: string | undefined,
    billingState: string | undefined,
    billingZip: string | undefined
  ) => {
    return [billingAddress, billingCity, billingState, billingZip]
      .filter((item) => item)
      .join(', ')
  }

  const getValue = (item: string) => {
    return item
  }

  const handleAddClick = () => {
    setOpenModal(true)
  }

  const handleEditClick = (rsId: number) => {
    location.href = `/admin/editReferralSourceContact/${rsId}`
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleSave = async (values: any, { setSubmitting }: any) => {
    setGridLoading(true)
    try {
      const newReferralSource: ReferralSourceDTO = {
        rsId: 0,
        sourceName: values.newSource,
        speciality: values.newSpeciality,
        billingAddress: values.newBillingAddress,
        billingCity: values.newBillingCity,
        billingState: values.newBillingState,
        billingZip: values.newBillingZip,
        referralSourceEmail: values.newReferralSourceEmail,
        billingPhone: values.newBillingPhone,
        billingFax: values.newBillingFax,
        inActive: false,
      }
      await addReferralSource(newReferralSource)
      handleCloseModal()
      await loadReferralSource()
    } finally {
      setGridLoading(false)
      setSubmitting(false)
    }
  }

  const setPaginationOffsetData = (totalItems: number) => {
    const firstIndex = (page - 1) * pageSize + 1
    const lastIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    setFirstRecordIndex(firstIndex)
    setLastRecordIndex(lastIndex)
  }

  const handlePageChange = (event: any) => {
    const newPage = event.selected + 1
    setPage(newPage)
    searchReferralSource(
      referralSpeciality.trim() || null,
      inActive,
      sourceName.trim() || null,
      newPage,
      pageSize
    )
  }

  const paginationInfo = () => {
    if (!isSearchGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {firstRecordIndex} to {lastRecordIndex} of {totalItems} entries
          </Typography>
        </div>
      )
    }
    return <></>
  }

  const loadReferralSource = async () => {
    setGridLoading(true)

    const response = await searchReferralSource(
      referralSpeciality.trim() || null,
      inActive,
      sourceName.trim() || null,
      page,
      pageSize
    )

    const specialities = await getReferralSourceSpecialties(null, 1, 5000)

    setSpecialityOptions((prev) => {
      return [
        ...prev,
        ...(specialities.items
          ?.map((s) => s.speciality)
          .filter((item): item is string => item !== undefined) || []),
      ]
    })

    const { items, totalItems, totalPages } = response

    setTotalItems(totalItems)
    setPageCount(totalPages)
    setGridData(items)

    if (totalItems > 0) {
      setPaginationOffsetData(totalItems)
    }
    setGridLoading(false)
  }

  const isSearchGridDataEmpty = () => {
    return !gridData || gridData.length === 0
  }

  const clearSearch = async () => {
    setReferralSpeciality('')
    setInActive(false)
    setSourceName('')
    setPage(1)
    setTotalItems(0)
    setPageCount(0)
    setGridData([])
    setGridLoading(false)
  }

  const search = async (event: any) => {
    event.preventDefault()
    setGridLoading(true)
    setPage(1)
    try {
      const response = await searchReferralSource(
        referralSpeciality.trim() || null,
        inActive,
        sourceName.trim() || null,
        page,
        pageSize
      )

      const { items, totalItems, totalPages } = response

      setTotalItems(totalItems)
      setPageCount(totalPages)
      setGridData(items)

      if (totalItems > 0) {
        setPaginationOffsetData(totalItems)
      }
    } finally {
      setGridLoading(false)
    }
  }

  const mapSearchGridData = () => {
    if (isSearchGridDataEmpty()) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    }

    return gridData.map((item, index) => (
      <TableRow className="gridPadding" key={index}>
        <TableCell width={'16.66%'}>{item.sourceName}</TableCell>
        <TableCell width={'16.66%'}>{item.speciality}</TableCell>
        <TableCell width={'16.66%'}>
          {formatBillingDetails(
            item.billingAddress,
            item.billingCity,
            item.billingState,
            item.billingZip
          )}
        </TableCell>
        <TableCell width={'16.66%'} style={{ textAlign: 'right', paddingRight: '250px' }}>
          {item.updatedDate ? moment.utc(item.updatedDate).format('MM/DD/YYYY') : ''}
        </TableCell>
        <TableCell width={'16.66%'}>{item.updatedByUser}</TableCell>
        <TableCell width={'16.66%'}>
          <Tooltip title="Edit Referral Source">
            <Edit
              color="primary"
              style={{ fontSize: '20px', cursor: 'pointer' }}
              onClick={() => handleEditClick(item.rsId)}
            />
          </Tooltip>
        </TableCell>
      </TableRow>
    ))
  }

  useEffect(() => {
    loadReferralSource()
  }, [page, pageSize])

  return (
    <>
      <Helmet>
        <title>Referral Source</title>
      </Helmet>
      <ResponsiveAppBar title="Referral Source" pageIcon={<GroupIcon />} />
      <Paper style={{ margin: '20px', height: '100%', paddingBottom: '20px' }}>
        {gridLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '2',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <form onSubmit={search}>
          <Grid container style={{ padding: '10px', width: '100%' }} xs={12}>
            <Typography
              color="primary"
              component="h6"
              variant="h6"
              gutterBottom
              style={{ paddingBottom: '10px' }}
            >
              SEARCH CRITERIA
            </Typography>
            <Grid container direction="row" spacing={2} style={{ width: '100%' }}>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <TextField
                    value={sourceName}
                    onChange={(e) => setSourceName(e.target.value)}
                    label="Referral Source"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ fullWidth: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <TextField
                    value={referralSpeciality}
                    onChange={(e) => setReferralSpeciality(e.target.value)}
                    label="Referral Speciality"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ fullWidth: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid style={{ paddingTop: '12px' }} item xs={3}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inActive}
                        onChange={(e) => setInActive(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Include Inactive"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={3} container justifyContent="flex-end">
                <Tooltip title="Add Referral Source">
                  <AddCircle
                    color="primary"
                    style={{ fontSize: '30px', cursor: 'pointer' }}
                    onClick={handleAddClick}
                  />
                </Tooltip>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={2}
              style={{
                marginLeft: '1px',
                marginTop: '10px',
                paddingTop: '5px',
                paddingBottom: '10px',
                width: '100%',
              }}
            >
              <Button
                type="submit"
                color="primary"
                size="medium"
                variant="contained"
                style={{ marginRight: '10px' }}
              >
                Search
              </Button>
              <Button
                color="secondary"
                onClick={clearSearch}
                size="medium"
                variant="contained"
              >
                Clear Search
              </Button>
            </Grid>
          </Grid>
        </form>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignContent="center"
          alignItems="flex-start"
          wrap="wrap"
        >
          <Grid item xs={12}>
            <Table aria-label="simple table" style={{ padding: '20px' }}>
              <TableHead>
                <TableRow className="gridPadding">
                  <TableCell style={{ width: '25%' }}>Source</TableCell>
                  <TableCell style={{ width: '25%' }}>Speciality</TableCell>
                  <TableCell style={{ width: '25%' }}>Billing Address</TableCell>
                  <TableCell
                    style={{ width: '25%', textAlign: 'right', paddingRight: '250px' }}
                  >
                    Updated Date
                  </TableCell>
                  <TableCell style={{ width: '20%', whiteSpace: 'nowrap' }}>
                    Updated By
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{mapSearchGridData()}</TableBody>
            </Table>
            <div className="pagination-row">
              {paginationInfo()}
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                onPageChange={handlePageChange}
                forcePage={page - 1}
                pageCount={pageCount}
                containerClassName={'pagination'}
                activeClassName={'active'}
                renderOnZeroPageCount={() => null}
              />
            </div>
          </Grid>
        </Grid>
      </Paper>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <ResponsiveAppBar title="Add Source" pageIcon={<GroupIcon />} isModal={true} />
        <Typography
          variant={'h6'}
          color="textSecondary"
          style={{
            height: '18px',
            paddingLeft: '20px',
            fontSize: '18px',
            paddingBottom: '20px',
          }}
        >
          REFERRAL DETAILS
        </Typography>
        <Formik
          initialValues={{
            newSource: '',
            newSpeciality: '',
            newBillingAddress: '',
            newBillingCity: '',
            newBillingState: '',
            newBillingZip: '',
            newReferralSourceEmail: '',
            newBillingPhone: '',
            newBillingFax: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSave}
        >
          {({ isSubmitting, isValid, setFieldValue, values }) => (
            <Form>
              <Grid container xs={12} direction="row">
                <Grid item xs={6}>
                  <DialogContent style={{ padding: '8px 16px' }}>
                    <FullWidthField
                      name="newSource"
                      label="Referral Source"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </DialogContent>
                </Grid>
                <Grid item xs={6}>
                  <DialogContent style={{ padding: '18px 16px' }}>
                    <SelectInput
                      inputId="newSpeciality"
                      getName={getValue}
                      getValue={getValue}
                      label="Referral Speciality"
                      name="newSpeciality"
                      fullWidth
                      shrinkLabel={true}
                      outlined={true}
                      multiple={false}
                      native={true}
                      items={specialityOptions}
                      onChange={(event) => {
                        setFieldValue('newSpeciality', event.target.value)
                      }}
                    />
                  </DialogContent>
                </Grid>
              </Grid>

              <Grid container direction="row">
                <Grid
                  container
                  item
                  xs={6}
                  direction="column"
                  style={{ paddingBottom: '15px' }}
                >
                  <Typography
                    variant={'h6'}
                    color="textSecondary"
                    style={{
                      height: '18px',
                      paddingLeft: '20px',
                      fontSize: '18px',
                      paddingBottom: '20px',
                    }}
                  >
                    BILLING DETAILS
                  </Typography>
                  <Grid item>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newBillingAddress"
                        label="Billing Address"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                  <Grid item>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newBillingCity"
                        label="Billing City"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        inputProps={{ maxLength: 30 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                  <Grid container item direction="row">
                    <Grid item xs={6}>
                      <Autocomplete
                        options={dropdownState}
                        size="small"
                        style={{ paddingTop: 16, paddingLeft: 16 }}
                        getOptionLabel={(option: string) => option}
                        value={dropdownState.find((opt) => opt === values.state) || null}
                        onChange={(_: any, newValue: string | null) =>
                          setFieldValue('state', newValue || '')
                        }
                        renderInput={(params: AutocompleteRenderInputParams) => (
                          <TextField
                            {...params}
                            label="State"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FullWidthField
                          name="newBillingZip"
                          label="Billing Zip"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          inputProps={{ maxLength: 10 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </DialogContent>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container item xs={6} direction="column">
                  <Typography
                    variant={'h6'}
                    color="textSecondary"
                    style={{
                      height: '18px',
                      paddingLeft: '20px',
                      fontSize: '18px',
                      paddingBottom: '20px',
                    }}
                  >
                    CONTACT DETAILS
                  </Typography>
                  <Grid item>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newReferralSourceEmail"
                        label="Referral Source Email"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                  <Grid item>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newBillingPhone"
                        label="Billing Phone"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputProps={{
                          inputComponent: PhoneNumberMaskOBM,
                          maxLength: 10,
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                  <Grid item>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newBillingFax"
                        label="Billing Fax"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputProps={{
                          inputComponent: PhoneNumberMaskOBM,
                          maxLength: 10,
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                </Grid>
              </Grid>
              <DialogActions>
                <Button onClick={handleCloseModal} color="primary">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting || !isValid}
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}

export default ReferralSource
